import { translate } from './../utils/locale';
import { getMATViewAs } from '../utils/impersonation';
import { isPublicRoute } from '../utils/isPublicRoute';
import { refreshAuthToken, TOKEN_EXPIRED_HEADER } from './refreshAuthToken';
import { refreshViewAsToken } from './refreshViewAsToken';

const controllers: { [key: string]: AbortController } = {};

interface Fetch {
	<T = any>(
		endpoint: string,
		options?: { [name: string]: any },
		useResponseBuilder?: false
	): Promise<T>;
	<T = any>(
		endpoint: string,
		options?: { [name: string]: any },
		useResponseBuilder?: true
	): Promise<ResponseBuilder<T>>;
}

const fetch: Fetch = (
	endpoint,
	{ body, abortId, ...customConfig } = {},
	useResponseBuilder = false
) => {
	let headers = {
		Accept: 'application/json',
		'Content-Type': 'application/json',
	};

	// Lets check if we have an impersonation token to send. If set this will come from viewing as via a MAT as it works per tab.
	const impersonationToken = sessionStorage.getItem('impersonationToken');

	if (impersonationToken) {
		headers['Authorization'] = 'Bearer ' + impersonationToken;
	}

	const { matImpersonateClientId } = getMATViewAs();
	if (matImpersonateClientId) {
		headers['viewAsClientId'] = matImpersonateClientId;
	}

	// If the feature exists, and an abortId is given
	if (AbortController && abortId) {
		// find any matching controller
		const currentController = controllers[abortId];

		// abort the request if found
		if (currentController) {
			currentController.abort();
		}

		controllers[abortId] = new AbortController();
	}

	// This request's own controller, as a newer request with the same abortId replaces the one in controllers
	const controller = abortId ? controllers[abortId] : undefined;

	// Once this request has finished (including any refresh and retry), forget its controller, unless a newer
	// request has already replaced it
	const releaseController = () => {
		if (abortId && controllers[abortId] === controller) {
			delete controllers[abortId];
		}
	};

	const config = {
		method: body ? 'POST' : 'GET',
		...customConfig,
		headers: {
			...headers,
			...customConfig.headers,
		},
		...(controller ? { signal: controller.signal } : {}),
		credentials: 'same-origin',
	};

	if (body) {
		//@ts-ignore
		config.body = JSON.stringify(body);
	}

	/**
	 * A view as token lives in session storage rather than the refresh cookie, so this tab gets a new one instead.
	 * If another request already replaced the view as token this one was sent with, just retry with the new one.
	 */
	const renewToken = (): Promise<boolean> => {
		if (!impersonationToken) {
			return refreshAuthToken();
		}

		const currentImpersonationToken = sessionStorage.getItem('impersonationToken');

		return currentImpersonationToken && currentImpersonationToken !== impersonationToken
			? Promise.resolve(true)
			: refreshViewAsToken();
	};

	const send = (canRefresh: boolean): Promise<Response> =>
		window.fetch(endpoint, { ...config } as RequestInit).then((response) => {
			if (response.ok) {
				// All good here, lets return the value
				return response;
			} else if (
				response.status === 401 &&
				canRefresh &&
				// Only refresh when the Connect JWT has expired. APIs that validate it with Alps.Web.Authentication
				// send this header on that 401; any other 401 isn't something a refresh can fix, and a failed
				// refresh would sign the user out.
				response.headers.get(TOKEN_EXPIRED_HEADER) !== null &&
				// Public routes (login, activate, etc.) expect a 401 from ConnectCtx.
				!isPublicRoute(window.location.pathname)
			) {
				// The JWT has expired: refresh it once and retry, otherwise the user has to log in again
				return renewToken().then((refreshed) => {
					if (!refreshed) {
						return redirectToLogin();
					}

					// A newer request with the same abortId cancelled this one while it was refreshing
					if (controller?.signal.aborted) {
						return Promise.reject(new DOMException('Aborted', 'AbortError'));
					}

					if (impersonationToken) {
						config.headers = {
							...config.headers,
							Authorization: 'Bearer ' + sessionStorage.getItem('impersonationToken'),
						};
					}

					return send(false);
				});
			} else if (response.status === 401 || response.status === 302) {
				return redirectToLogin();
			} else if (response.status === 403) {
				return Promise.reject(response.statusText);
			} else {
				return response;
			}
		});

	return send(true)
		.finally(releaseController)
		.then(async (response: any) => {
			// Note: The config.headers['Content-Type'] !== 'application/json' check is currently causing issues with the PDF downloads
			// Ideally, this check should just be: customConfig.responseType == 'blob'
			// Ticket #48131 has been created to address the issue
			if (
				config.headers['Content-Type'] !== 'application/json' ||
				customConfig.responseType == 'blob'
			) {
				return response.blob();
			}

			// Checks to see for too many attempts
			if (response.status === 429) {
				const retryAfter = response.headers.get('retry-after');
				const message = translate('auth.login.messages.TOO_MANY_ATTEMPTS', {
					statusText: response.statusText,
					retryAfter: retryAfter,
				});

				return Promise.reject(retryAfter ? message : response.statusText);
			}
			// Check if the response was ok but there was a redirect such as logout
			if (response.status === 200 && response.redirected) {
				return Promise.resolve('');
			}

			if (response.status != 200 && !useResponseBuilder) {
				var jsonMessage = await response.json();
				return Promise.reject(jsonMessage.Message);
			} else {
				try {
					// if the response has no body, this will fail
					var jsonMessage = await response.json();

					return jsonMessage;
				} catch (e) {
					return Promise.resolve();
				}
			}
		});
};

/**
 * Handle an expired session: redirect to login with a return URL
 */
const redirectToLogin = (): Promise<never> => {
	const currentPath = window.location.pathname + window.location.search;
	const returnUrl = encodeURIComponent(currentPath);
	const loginUrl = `/auth/login?ReturnUrl=${returnUrl}`;

	// Only capture return URL and redirect for authenticated routes.
	// Public routes (login, activate, etc.) expect a 401 from ConnectCtx and
	// should not be treated as a destination to return to after re-login.
	if (!isPublicRoute(window.location.pathname)) {
		sessionStorage.setItem('authReturnUrl', currentPath);
		window.location.href = loginUrl;
	}

	return Promise.reject('Unauthorized');
};

export default fetch;
